import { useCallback } from 'react';

import type { InventoryQuerySchema } from '@/_pages/settings/inventory-kits/shared/schemas/inventory';

import { useBulkUpdateInventoryMutation } from '../apis';

export function useSubInventoryItems() {
  const bulkUpdateMutation = useBulkUpdateInventoryMutation();

  const updateSubInventories = useCallback(
    async ({
      inventoryId,
      parentId,
      isChildrenDirty,
      existingChildren,
      newChildren,
    }: {
      inventoryId: number;
      parentId?: number | null;
      existingChildren?: InventoryQuerySchema['children'];
      newChildren: InventoryQuerySchema['children'];
      isChildrenDirty: boolean;
    }) => {
      const oldChildren = existingChildren || [];
      const children = newChildren || [];

      let payload: { id: number; parent_id: number | null; sort_order: number | null }[] = [];
      if (parentId) {
        // Remove the parent from the children
        payload = oldChildren.map((child) => ({
          id: child.id,
          parent_id: null,
          sort_order: null,
        }));
      } else if (isChildrenDirty && children) {
        const removedChildren = oldChildren.filter((child) => !children.some((c) => c.id === child.id));
        const addedChildren = [...children];

        payload = [
          ...removedChildren.map((child) => ({
            id: child.id,
            parent_id: null,
            sort_order: null,
          })),
          ...addedChildren.map((child, index) => ({
            id: child.id,
            parent_id: inventoryId,
            sort_order: index,
          })),
        ];
      }

      if (payload.length > 0) {
        await bulkUpdateMutation.mutateAsync({ items: payload });
      }
    },
    [bulkUpdateMutation]
  );

  return {
    updateSubInventories,
    isSubInventoriesMutationPending: bulkUpdateMutation.isPending,
  };
}
